import type { Move, RoundResultView } from '@shared/protocol/messages';
import type { BattleState, BattleWinner } from './engine';
import { RULES } from './rules';
import type { Rng } from './rng';
import { counterOf } from './rps';

/**
 * Single-player battle twists, so a fight is more than picking a hand:
 *
 * - Tells: before some rounds the monster gives its move away... or bluffs.
 *   A bluff shows the hand its real move beats, catching anyone who counters
 *   the tell. Sharper monsters bluff more; an angry boss bluffs more still.
 * - Bosses (chapter bosses, tower boss floors, the weekly boss) charge up a
 *   big hit every few rounds (the Focus stance, announced), get angry at half
 *   HP (more attack, more bluffs, faster charges) and show it.
 * - Page events: now and then something happens to the notebook page for
 *   one round (a gold star, ink rain, a double page...), announced before
 *   the hands are picked.
 *
 * Everything is decided from previous rounds only, when the monster commits
 * its move, and settled after the engine resolved the round, so the shared
 * engine (and PvP) is untouched.
 */

export type PageEvent = 'gold_star' | 'ink_rain' | 'double_page' | 'tie_sting' | 'shuffle';
export const PAGE_EVENTS: readonly PageEvent[] = ['gold_star', 'ink_rain', 'double_page', 'tie_sting', 'shuffle'];

export type Tell =
  /** "It's winding up a Rock!" — true, or a bluff covering the hand that beats your counter. */
  | { kind: 'move'; move: Move; honest: boolean }
  /** A boss charging a big hit (it plays Focus this round). */
  | { kind: 'charge' }
  /** Full ink: the monster unleashes its ultimate this round. */
  | { kind: 'ultimate' };

/** What the player is told before picking a hand. */
export interface RoundPreview {
  tell: Tell | null;
  event: PageEvent | null;
  /** The boss is angry (half HP or less). */
  rage: boolean;
  /** It got angry just now (show it once). */
  rageStarted: boolean;
}

/** What a page event did after the round (index 0 = hero). */
export interface TwistResult {
  event: PageEvent;
  heal: [number, number];
  extra: [number, number];
  ink: [number, number];
  /** HP after the event. */
  hp: [number, number];
}

export const TWISTS = {
  /** Chance (%) of a move tell in a round. */
  tellPercent: { normal: 30, boss: 45 },
  /** Chance (%) a tell is true, by AI level (see aiLevelFor). */
  honestPercent: [90, 75, 65, 55],
  /** An angry boss tells the truth this much less often. */
  rageHonestyDrop: 10,
  /** An angry boss hits this much harder. */
  rageAtkPercent: 25,
  /** A boss charges a big hit every N rounds (fewer when angry). */
  chargeEvery: { calm: 4, rage: 3 },
  /** Chance (%) of a page event in a round, from round `eventFrom`, at least `eventGap` rounds apart. */
  eventPercent: 25,
  eventFrom: 2,
  eventGap: 3,
  goldStarHealPercent: 15,
  inkRain: 25,
  tieStingPercent: 6,
  /** Ink Strike (ui/traceGame.ts): chance (%) after a won round, seconds to trace, best extra hit (% of the monster's max HP), lowest score that still hits. */
  trace: { chance: 25, seconds: 5, maxPercent: 12, minScore: 0.25 },
} as const;

/** Extra damage for an Ink Strike traced with this score (0..1); 0 below the minimum. */
export function traceDamage(foeMaxHp: number, score: number): number {
  if (score < TWISTS.trace.minScore) return 0;
  return Math.max(1, Math.floor((foeMaxHp * TWISTS.trace.maxPercent * Math.min(1, score)) / 100));
}

/** A tell for the monster's committed move: usually true, sometimes a bluff. */
export function planTell(committed: Move, aiLevel: number, boss: boolean, rage: boolean, rng: Rng): Tell | null {
  if (rng.int(100) >= (boss ? TWISTS.tellPercent.boss : TWISTS.tellPercent.normal)) return null;
  const honest = TWISTS.honestPercent[Math.min(aiLevel, TWISTS.honestPercent.length - 1)]! - (rage ? TWISTS.rageHonestyDrop : 0);
  if (rng.int(100) < honest) return { kind: 'move', move: committed, honest: true };
  // Show the hand the real move beats: whoever counters the tell walks into it.
  return { kind: 'move', move: counterOf(committed), honest: false };
}

/** Is a boss charge due this round (rounds counted from 1)? */
export function chargeDue(round: number, rage: boolean): boolean {
  return round > 1 && round % (rage ? TWISTS.chargeEvery.rage : TWISTS.chargeEvery.calm) === 0;
}

/** A page event for this round, or null. */
export function pickEvent(round: number, lastEventRound: number, rng: Rng): PageEvent | null {
  if (round < TWISTS.eventFrom || round - lastEventRound < TWISTS.eventGap) return null;
  if (rng.int(100) >= TWISTS.eventPercent) return null;
  return PAGE_EVENTS[rng.int(PAGE_EVENTS.length)]!;
}

/** The battle's winner from its HP after an event changed it (same rules as the engine). */
export function settle(state: BattleState, hp: readonly [number, number]): BattleWinner {
  if (hp[0] === 0 || hp[1] === 0) return hp[0] === 0 ? 1 : 0;
  if (state.round >= state.maxRounds) {
    const a = hp[0] * state.fighters[1].maxHp;
    const b = hp[1] * state.fighters[0].maxHp;
    return a === b ? 'draw' : a > b ? 0 : 1;
  }
  return null;
}

/** Applies a page event after the round the engine just resolved. */
export function applyEvent(state: BattleState, r: RoundResultView, event: PageEvent): { state: BattleState; result: TwistResult } {
  const hp: [number, number] = [state.hp[0], state.hp[1]];
  const ink: [number, number] = [state.ink[0], state.ink[1]];
  const heal: [number, number] = [0, 0];
  const extra: [number, number] = [0, 0];
  const gained: [number, number] = [0, 0];
  const maxHp = [state.fighters[0].maxHp, state.fighters[1].maxHp] as const;
  const w = r.winner;
  if (event === 'gold_star' && w !== -1 && hp[w] > 0) {
    heal[w] = Math.min(maxHp[w] - hp[w], Math.max(1, Math.floor((maxHp[w] * TWISTS.goldStarHealPercent) / 100)));
    hp[w] += heal[w];
  } else if (event === 'double_page' && w !== -1 && r.damage > 0) {
    // The hit lands again on the facing page.
    const l = w === 0 ? 1 : 0;
    extra[l] = Math.min(hp[l], r.damage);
    hp[l] -= extra[l];
  } else if (event === 'tie_sting' && w === -1) {
    // A paper cut for both; it never knocks anyone out.
    for (const i of [0, 1] as const) {
      extra[i] = Math.min(Math.max(0, hp[i] - 1), Math.max(1, Math.floor((maxHp[i] * TWISTS.tieStingPercent) / 100)));
      hp[i] -= extra[i];
    }
  } else if (event === 'ink_rain') {
    for (const i of [0, 1] as const) {
      gained[i] = Math.min(RULES.ink.max, ink[i] + TWISTS.inkRain) - ink[i];
      ink[i] += gained[i];
    }
  }
  const winner = state.winner !== null && hp[0] > 0 && hp[1] > 0 ? state.winner : settle(state, hp);
  return { state: { ...state, hp, ink, winner }, result: { event, heal, extra, ink: gained, hp: [hp[0], hp[1]] } };
}
